import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import RichEditor from "../components/RichEditor";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import ClientCombobox from "../components/ClientCombobox";
import { PRIORITIES, PRIORITY_LABEL, CATEGORIES, CATEGORY_LABEL } from "../lib/labels";
import { toast } from "sonner";
import { Sparkle, Paperclip, X } from "@phosphor-icons/react";

export default function CreateTicket() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isStaff = user?.role === "admin" || user?.role === "technicien";

  const [form, setForm] = useState({
    subject: "", description: "", category: "autre", priority: "normale",
    client_email: "", client_name: "",
    client_first_name: "", client_last_name: "", client_company: "", client_phone: "",
  });
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("__new");
  const [busy, setBusy] = useState(false);
  const [files, setFiles] = useState([]);

  const addFiles = (list) => {
    const incoming = Array.from(list || []);
    const merged = [...files, ...incoming].slice(0, 10);
    setFiles(merged);
  };
  const removeFile = (idx) => setFiles(files.filter((_, i) => i !== idx));

  useEffect(() => {
    if (isStaff) api.get("/users", { params: { role: "client" } }).then((r) => setClients(r.data));
  }, [isStaff]);

  const detectUrgency = async () => {
    if (!form.subject && !form.description) return;
    try {
      const r = await api.post("/ai/detect-urgency", { subject: form.subject, description: form.description });
      setForm((f) => ({ ...f, priority: r.data.priority || "normale" }));
      toast.info(`Priorité suggérée : ${PRIORITY_LABEL[r.data.priority] || r.data.priority}. ${r.data.reason || ""}`);
    } catch { toast.error("Analyse IA indisponible"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        subject: form.subject, description: form.description,
        category: form.category, priority: form.priority,
      };
      if (isStaff) {
        if (selectedClient !== "__new") body.client_id = selectedClient;
        else {
          body.client_first_name = form.client_first_name;
          body.client_last_name = form.client_last_name;
          body.client_name = [form.client_first_name, form.client_last_name].filter(Boolean).join(" ").trim() || form.client_name;
          body.client_email = form.client_email.trim();
          if (!body.client_email) {
            const who = body.client_name || form.client_company;
            if (!who) { toast.error("Indiquez au moins le nom ou l'email du client"); setBusy(false); return; }
            const slug = who.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, ".").replace(/^\.+|\.+$/g, "") || "sans-email";
            body.client_email = `${slug}.${Date.now().toString(36)}@no-email.masterticket.io`;
          }
          body.client_company = form.client_company;
          body.client_phone = form.client_phone;
        }
      }
      const r = await api.post("/tickets", body);
      // Upload attachments if any (authenticated endpoint)
      if (files.length > 0) {
        for (const f of files) {
          try {
            const fd = new FormData();
            fd.append("file", f);
            await api.post(`/tickets/${r.data.id}/attachments`, fd, {
              headers: { "Content-Type": "multipart/form-data" },
            });
          } catch (upErr) {
            toast.error(`Pièce jointe « ${f.name} » : ${upErr.response?.data?.detail || "échec upload"}`);
          }
        }
      }
      toast.success(`Ticket #${r.data.number} créé${files.length ? ` · ${files.length} pièce(s) jointe(s)` : ""}`);
      nav(`/tickets/${r.data.id}`);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map((x) => x.msg).join(" — ") : (typeof d === "string" ? d : "Erreur lors de la création du ticket"));
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <div className="label-caps">Nouveau</div>
        <h1 className="font-display text-3xl font-bold mt-1">Créer un ticket</h1>
      </div>
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={submit} className="space-y-5">
            {isStaff && (
              <div>
                <Label>Client</Label>
                <ClientCombobox
                  clients={clients}
                  value={selectedClient}
                  onChange={setSelectedClient}
                  allowNew
                />
                {selectedClient === "__new" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                    <Input placeholder="Prénom" data-testid="new-client-first-name"
                      value={form.client_first_name} onChange={(e) => setForm({ ...form, client_first_name: e.target.value })}/>
                    <Input placeholder="Nom" data-testid="new-client-last-name"
                      value={form.client_last_name} onChange={(e) => setForm({ ...form, client_last_name: e.target.value })}/>
                    <Input placeholder="Société" data-testid="new-client-company"
                      value={form.client_company} onChange={(e) => setForm({ ...form, client_company: e.target.value })}/>
                    <Input placeholder="Téléphone" data-testid="new-client-phone"
                      value={form.client_phone} onChange={(e) => setForm({ ...form, client_phone: e.target.value })}/>
                    <Input placeholder="Email du client (facultatif)" type="email" data-testid="new-client-email"
                      className="md:col-span-2"
                      list="known-client-emails"
                      value={form.client_email} onChange={(e) => {
                        const v = e.target.value;
                        setForm({ ...form, client_email: v });
                        const match = clients.find((c) => c.email.toLowerCase() === v.toLowerCase());
                        if (match) {
                          setSelectedClient(match.id);
                          const [fn, ...rest] = (match.full_name || "").split(" ");
                          setForm((f) => ({
                            ...f,
                            client_first_name: fn || "",
                            client_last_name: rest.join(" "),
                            client_company: match.company || "",
                            client_phone: match.phone || "",
                            client_email: match.email,
                          }));
                          toast.info(`Client existant sélectionné : ${match.full_name}`);
                        }
                      }}/>
                    <datalist id="known-client-emails">
                      {clients.map((c) => (
                        <option key={c.id} value={c.email}>{c.full_name}{c.company ? ` — ${c.company}` : ""}</option>
                      ))}
                    </datalist>
                  </div>
                )}
              </div>
            )}

            <div>
              <Label htmlFor="subject">Sujet</Label>
              <Input id="subject" data-testid="ticket-subject" required
                value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}/>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="description">Description</Label>
                <Button type="button" variant="ghost" size="sm" data-testid="detect-urgency" onClick={detectUrgency}>
                  <Sparkle size={14} className="mr-1.5"/> Détecter l&apos;urgence
                </Button>
              </div>
              <Textarea id="description" data-testid="ticket-description" required
                className="hidden"
                value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}/>
              <RichEditor
                value={form.description}
                onChange={(v) => setForm({ ...form, description: v })}
                minHeight={200}
                placeholder="Décrivez votre problème (mise en forme, images, listes)…"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Catégorie</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="new-category"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{CATEGORY_LABEL[c]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priorité</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger data-testid="new-priority"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{PRIORITY_LABEL[p]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Pièces jointes <span className="text-xs text-muted-foreground">(optionnel — max 10 fichiers)</span></Label>
              <div className="mt-2 flex items-center gap-3 flex-wrap">
                <label className="inline-flex items-center gap-2 px-3 py-2 border rounded-md text-sm font-medium hover:bg-muted cursor-pointer" data-testid="add-files">
                  <Paperclip size={14}/> Ajouter des fichiers
                  <input type="file" multiple className="hidden"
                    onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
                    data-testid="ticket-file-input"/>
                </label>
                <span className="text-xs text-muted-foreground">{files.length === 0 ? "Aucun fichier sélectionné" : `${files.length} fichier(s) sélectionné(s)`}</span>
              </div>
              {files.length > 0 && (
                <ul className="mt-3 space-y-1.5" data-testid="ticket-file-list">
                  {files.map((f, i) => (
                    <li key={i} className="flex items-center justify-between gap-3 px-3 py-1.5 border rounded-md text-sm bg-muted/40">
                      <span className="truncate flex-1">{f.name} <span className="text-xs text-muted-foreground">· {(f.size / 1024).toFixed(1)} Ko</span></span>
                      <button type="button" onClick={() => removeFile(i)} className="text-muted-foreground hover:text-destructive" data-testid={`remove-file-${i}`} aria-label="Retirer">
                        <X size={14}/>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="flex items-center gap-3 justify-end">
              <Button type="button" variant="secondary" onClick={() => nav(-1)} data-testid="cancel-btn">Annuler</Button>
              <Button type="submit" disabled={busy} data-testid="submit-ticket">
                {busy ? "Création…" : "Créer le ticket"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
